import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import { actesEn } from '@/lib/actesCountry';
import {
  actesAuthorities, actesSaveAuthority, actesDeleteAuthority, actesMergeAuthority, actesTurnaround, type ActesTurnaround,
  actesLookupAuthority, actesQueue, type ActesQueue, type ActesRef, type ActesReference,
} from '@/integrations/actesApi';

/**
 * L'ANNUAIRE DES AUTORITÉS — the mairies, archives and civil-status services
 * every demande is addressed to.
 *
 * It is a directory before it is a table: grouped by kind, archives split by
 * their own sort, and led by the eight that carry the most work — because the
 * question people actually ask here is "who do we write to most, and do we
 * have their e-mail". The one missing e-mail is called out in the row, and the
 * web lookup fills ONLY the fields that are empty, as a proposal to confirm.
 */
const CATEGORIES = [
  { key: 'scec', color: 'violet', fr: "Service Central de l'État Civil", en: 'Central Civil Status Service' },
  { key: 'mairie', color: 'sky', fr: 'Mairies', en: 'Town halls' },
  { key: 'archives', color: 'amber', fr: 'Archives', en: 'Archives' },
  { key: 'foreign', color: 'rose', fr: 'Autorités Étrangères', en: 'Foreign authorities' },
  { key: 'other', color: 'slate', fr: 'Autres autorités', en: 'Other authorities' },
] as const;

const ARCHIVE_SUBS = [
  { key: 'departementale', fr: 'Départementales', en: 'Departmental' },
  { key: 'nationale', fr: 'Nationales', en: 'National' },
  { key: 'municipale', fr: 'Municipales', en: 'Municipal' },
  { key: 'diplomatique', fr: 'Diplomatiques', en: 'Diplomatic' },
  { key: 'autre', fr: 'Autres archives', en: 'Other archives' },
] as const;

export function AuthoritiesPanel({ lang, reference }: { lang: Lang; reference: ActesReference }) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('');
  const [edit, setEdit] = useState<ActesRef | null | 'new'>(null);
  const [batch, setBatch] = useState(false);
  /* Folded by default is wrong here — you would open the page to a wall of
     closed doors. Everything is open, and closing one is remembered. */
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setShut((p) => ({ ...p, [k]: !p[k] }));

  const list = useQuery<ActesRef[]>({
    queryKey: ['actes', 'authorities'],
    queryFn: ({ signal }) => actesAuthorities(signal),
    staleTime: 5 * 60_000,
  });
  const rows = list.data ?? [];
  const count = (r: ActesRef) => r.request_count ?? 0;

  /* The ones worth chasing first: no e-mail, most demandes on their desk. */
  const missingEmail = useMemo(
    () => rows.filter((r) => !r.email).sort((a, b) => count(b) - count(a)),
    [rows],
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (cat && (r.category ?? 'other') !== cat) return false;
      if (!q) return true;
      return [r.label_fr, r.label_en, r.department, r.email]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, search, cat]);

  const top = useMemo(
    () => rows.filter((r) => count(r) > 0).sort((a, b) => count(b) - count(a)).slice(0, 8),
    [rows],
  );
  const maxCount = top.length ? count(top[0]) : 1;
  /* Le total sur lequel la maquette compare la première autorité. */
  const totalReq = useMemo(() => rows.reduce((a, r) => a + count(r), 0), [rows]);

  /* CE QUI EST ENCORE CHEZ ELLES — la même liste que le tableau de bord,
     regroupée par autorité. `sitting` = parti, jamais revenu, le plus vieux
     devant. On ne demande rien de nouveau : c'est la réponse déjà en cache. */
  const queue = useQuery<ActesQueue>({
    queryKey: ['actes', 'queue', reference.me.id],
    queryFn: ({ signal }) => actesQueue(signal),
    staleTime: 60_000,
    retry: false,
  });
  /* LES DÉLAIS MESURÉS. Seules les autorités avec au moins trois demandes
     complètes (départ ET réception) apparaissent, avec le nombre derrière
     chaque médiane — quatre autorités au 14 septembre 2026, et la liste
     s'allonge seule depuis que le départ se pose au changement de statut. */
  const turnaround = useQuery<ActesTurnaround>({
    queryKey: ['actes', 'turnaround'],
    queryFn: ({ signal }) => actesTurnaround(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const measured = useMemo(() => {
    const t = turnaround.data;
    if (!t) return [];
    return Object.entries(t.authorities)
      .filter(([, v]) => v.n >= 3)
      .map(([id, v]) => ({ id, name: rows.find((r) => String(r.id) === id)?.label_fr ?? `#${id}`, ...v }))
      .sort((a, b) => b.n - a.n || a.median - b.median)
      .slice(0, 8);
  }, [turnaround.data, rows]);

  const waiting = useMemo(() => {
    const by = new Map<string, { name: string; open: number; oldest: number }>();
    for (const r of queue.data?.sitting ?? []) {
      const name = r.authority_label_fr || T('Sans autorité', 'No authority');
      const d = r.date_demande ? Math.round((Date.now() - new Date(`${r.date_demande}T00:00:00Z`).getTime()) / 86_400_000) : 0;
      const cur = by.get(name) || { name, open: 0, oldest: 0 };
      cur.open += 1;
      cur.oldest = Math.max(cur.oldest, d);
      by.set(name, cur);
    }
    return [...by.values()].sort((a, b) => b.oldest - a.oldest).slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.data]);

  const row = (r: ActesRef) => (
    <li key={r.id}>
      <div className="actes-list-main">
        <strong>{r.label_fr}</strong>
        <em className="actes-auth-meta">
          {r.department && <span><KBIcon name="pin" size={11} /> {r.department}</span>}
          {r.email
            ? <a href={`mailto:${r.email}`}><KBIcon name="mail" size={11} /> {r.email}</a>
            : <span className="miss">{T('e-mail manquant', 'email missing')}</span>}
          {r.phone && <span className="tel">{r.phone}</span>}
        </em>
      </div>
      <span className="actes-count-chip" title={T('Demandes', 'Requests')}>
        {count(r)} {T('demandes', 'req')}
      </span>
      {reference.me.isStaff && (
        <button className="actes-row-act" title={T('Modifier', 'Edit')} onClick={() => setEdit(r)}>
          <KBIcon name="edit" size={13} />
        </button>
      )}
    </li>
  );

  return (
    <section className="actes-panel v7">
      <div className="phead">
        <h2>{T('Annuaire des autorités', 'Authorities directory')}</h2>
        {reference.me.isStaff && (
          <div className="right">
            {missingEmail.length > 0 && (
              <button className="btn" onClick={() => setBatch(true)}>
                <KBIcon name="globe" size={13} /> {T('Compléter via le web', 'Fill via web')}
              </button>
            )}
            <button className="btn btn-p" onClick={() => setEdit('new')}>
              <KBIcon name="plus" size={13} /> {T('Nouvelle autorité', 'New authority')}
            </button>
          </div>
        )}
      </div>

      <div className="grid-2">
        {top.length > 0 && (
          <div className="card">
            <div className="card-h">
              <h3>{T('Les plus sollicitées', 'Most requested')}</h3>
              <span className="c sub">
                {T(`${count(top[0])} sur ${totalReq} vont au même bureau`, `${count(top[0])} of ${totalReq} go to one office`)}
              </span>
            </div>
            <div className="card-b">
              <ul className="rank">
                {top.map((r) => {
                  const w = Math.max(1.5, (count(r) / maxCount) * 100);
                  const inside = w > 22;
                  return (
                    <li key={r.id}>
                      <span className="lb" title={r.label_fr}>{r.label_short_fr || r.label_fr}</span>
                      <span className="tr">
                        <i style={{ width: `${w}%` }}>
                          <b style={inside
                            ? { right: 7 }
                            : { left: 'calc(100% + 0.4375rem)', color: 'var(--muted)' }}>{count(r)}</b>
                        </i>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* ── « COMBIEN DE TEMPS ILS METTENT » ──
            La maquette met ici un délai médian par autorité. Il ne sort pas de
            rien : il faudrait, sur la même demande, une date de départ ET une
            date de réception. Il y en a 95 sur 4 685 (septembre 2026) — pas
            de quoi donner un chiffre pour chaque mairie, et une médiane sur
            deux cas serait un chiffre inventé avec l'air d'un fait. Alors on
            montre ce qui EST vrai : ce qui est encore dehors, et depuis quand ;
            puis, plus bas, les seules autorités mesurées sur trois demandes ou
            plus, avec le nombre à côté. La liste s'allonge seule. */}
        <div className="card">
          <div className="card-h">
            <h3>{T('Ce qui est encore chez elles', 'What is still with them')}</h3>
            <span className="c sub">{T('vos demandes', 'your requests')}</span>
          </div>
          {waiting.length === 0 ? (
            <div className="empty"><b>{T('Rien dehors', 'Nothing out')}</b>{T('Aucune de vos demandes n’attend chez une autorité.', 'None of your requests is sitting with an authority.')}</div>
          ) : (
            <ul className="ag">
              {waiting.map((w) => (
                <li key={w.name}>
                  <span className="nm">
                    <b>{w.name}</b>
                    <span>{T(`${w.open} en cours`, `${w.open} open`)}</span>
                  </span>
                  <span className={`d ${w.oldest >= 45 ? 'late' : ''}`}>{T(`${w.oldest} j`, `${w.oldest} days`)}</span>
                </li>
              ))}
            </ul>
          )}
          {measured.length > 0 && (
            <>
              <div className="card-h" style={{ borderTop: '1px solid var(--line-2)' }}>
                <h3>{T('Délais mesurés', 'Measured turnaround')}</h3>
                <span className="c sub">{T('médiane · 3 demandes ou plus', 'median · 3+ requests')}</span>
              </div>
              <ul className="ag">
                {measured.map((m) => (
                  <li key={m.id}>
                    <span className="nm">
                      <b>{m.name}</b>
                      <span>{T(`${m.n} demandes complètes · 3 sur 4 sous ${m.p75} j`, `${m.n} completed · 3 in 4 under ${m.p75} d`)}</span>
                    </span>
                    <span className="d">{T(`${m.median} j`, `${m.median} days`)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {missingEmail.length > 0 && (
            <div className="empty" style={{ borderTop: '1px solid var(--line-2)' }}>
              {T(`${missingEmail.length} autorités n’ont pas d’e-mail enregistré. Ces demandes repassent par la poste.`,
                 `${missingEmail.length} authorities have no e-mail on file. Those requests fall back to post.`)}
            </div>
          )}
        </div>
      </div>

      <div className="toolbar" style={{ marginTop: 18 }}>
        <label className="srch">
          <KBIcon name="search" size={15} />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={T('Nom, département ou e-mail…', 'Name, department or email…')} />
          {search && <button className="x" onClick={() => setSearch('')} aria-label="clear">×</button>}
        </label>
        <span className="vr" />
        <label className="hint">
          {T('Catégorie', 'Category')}
          <select className="sel" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">{T('Toutes', 'All')}</option>
            {CATEGORIES.map((c) => {
              const n = rows.filter((r) => (r.category ?? 'other') === c.key).length;
              return n === 0 ? null : (
                <option key={c.key} value={c.key}>{T(c.fr, c.en)} ({n})</option>
              );
            })}
          </select>
        </label>
        {(search || cat) && (
          <button className="rb" title={T('Effacer les filtres', 'Clear the filters')}
            onClick={() => { setSearch(''); setCat(''); }}>
            <KBIcon name="trash" size={15} />
          </button>
        )}
        <span className="res mono">{shown.length} {T('résultats', 'results')}</span>
      </div>

      {list.isLoading ? (
        <p className="muted-note">{T('Chargement…', 'Loading…')}</p>
      ) : shown.length === 0 ? (
        <div className="estate">
          <span className="ic"><KBIcon name="building" size={22} /></span>
          <h3>{T('Aucune autorité', 'No authority')}</h3>
          <p>{T('Rien ne correspond à cette recherche.', 'Nothing matches this search.')}</p>
        </div>
      ) : CATEGORIES.map((c) => {
        const items = shown.filter((r) => (r.category ?? 'other') === c.key);
        if (!items.length) return null;
        return (
          <div key={c.key} className="actes-auth-group">
            <button type="button" className={`actes-auth-head ${shut[c.key] ? 'shut' : ''}`}
              aria-expanded={!shut[c.key]} onClick={() => toggle(c.key)}>
              <KBIcon name="chevron" size={13} />
              <span className={`actes-tag c-${c.color}`}>{T(c.fr, c.en)}</span>
              <span className="dim">{items.length}</span>
            </button>
            {shut[c.key] ? null : c.key === 'archives' ? ARCHIVE_SUBS.map((s) => {
              const sub = items.filter((r) => (r.subcategory ?? 'autre') === s.key);
              if (!sub.length) return null;
              const k = `archives:${s.key}`;
              return (
                <div key={s.key} className="actes-auth-sub">
                  <button type="button" className={`actes-auth-subhead ${shut[k] ? 'shut' : ''}`}
                    aria-expanded={!shut[k]} onClick={() => toggle(k)}>
                    <KBIcon name="chevron" size={11} /> {T(s.fr, s.en)} · {sub.length}
                  </button>
                  {!shut[k] && <div className="actes-block"><ul className="actes-list tight">{sub.map(row)}</ul></div>}
                </div>
              );
            }) : (
              <div className="actes-block"><ul className="actes-list tight">{items.map(row)}</ul></div>
            )}
          </div>
        );
      })}

      {edit && (
        <AuthorityModal
          lang={lang} reference={reference} all={rows}
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); void qc.invalidateQueries({ queryKey: ['actes'] }); }}
        />
      )}
      {batch && (
        <BatchLookupModal
          lang={lang} candidates={missingEmail}
          onClose={() => setBatch(false)}
          onSaved={() => { setBatch(false); void qc.invalidateQueries({ queryKey: ['actes'] }); }}
        />
      )}
    </section>
  );
}

/* ── One authority ──────────────────────────────────────────────────────── */
function AuthorityModal({ lang, reference, all, initial, onClose, onSaved }: {
  lang: Lang; reference: ActesReference; all: ActesRef[];
  initial: ActesRef | null; onClose: () => void; onSaved: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const editing = !!initial;
  const [label, setLabel] = useState(initial?.label_fr ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'other');
  const [subcategory, setSubcategory] = useState(initial?.subcategory ?? 'autre');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [department, setDepartment] = useState(initial?.department ?? '');
  const [mergeInto, setMergeInto] = useState('');
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);

  /* The lookup only OFFERS: a field already filled in by a person is never
     overwritten by the model. */
  const webLookup = async () => {
    if (!label.trim()) return;
    setLooking(true); setNote(''); setErr('');
    try {
      const p = await actesLookupAuthority(label.trim());
      setEmail((v) => v || p.email || '');
      setPhone((v) => v || p.phone || '');
      setAddress((v) => v || p.address || '');
      setDepartment((v) => v || p.department || '');
      const found = !!(p.email || p.phone || p.address || p.department);
      setNote(found
        ? `${T('Trouvé — à vérifier', 'Found — please verify')}${p.source ? ` — ${p.source}` : ''}`
        : T('Rien trouvé de source fiable.', 'Nothing found from a reliable source.'));
    } catch (e) { setErr(friendlyError(e, lang)); }
    setLooking(false);
  };

  const save = async () => {
    if (!label.trim()) { setErr(T('Le nom est obligatoire.', 'The name is required.')); return; }
    setBusy(true); setErr('');
    try {
      await actesSaveAuthority(initial?.id ?? null, {
        label_fr: label.trim(), label_en: label.trim(), category,
        subcategory: category === 'archives' ? subcategory : '',
        email: email.trim(), phone: phone.trim(), address: address.trim(), department: department.trim(),
      });
      onSaved();
    } catch (e) { setErr(friendlyError(e, lang)); setBusy(false); }
  };

  const remove = async () => {
    if (!initial) return;
    setBusy(true); setErr('');
    try { await actesDeleteAuthority(initial.id); onSaved(); }
    catch (e) {
      const m = e instanceof Error ? e.message : '';
      setErr(m === 'in_use'
        ? T('Utilisée par des demandes — fusionnez plutôt.', 'Used by requests — merge instead.')
        : friendlyError(e, lang));
      setConfirmDelete(false); setBusy(false);
    }
  };

  const merge = async () => {
    if (!initial || !mergeInto) return;
    setBusy(true); setErr('');
    try { await actesMergeAuthority(initial.id, Number(mergeInto)); onSaved(); }
    catch (e) { setErr(friendlyError(e, lang)); setConfirmMerge(false); setBusy(false); }
  };

  return createPortal(
    <div className="svc-modal-backdrop actes-skin" onClick={onClose}>
      <div className="svc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 620 }}>
        <button className="svc-modal-close" onClick={onClose}>×</button>
        <div className="svc-modal-body">
          <div className="svc-modal-tag">{T('Autorité', 'Authority')}</div>
          <h2>{editing ? T("Modifier l'autorité", 'Edit authority') : T('Nouvelle autorité', 'New authority')}</h2>
          <div className="actes-grid2">
            <label className="actes-field">
              <span>{T('Nom', 'Name')}</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
            </label>
            <label className="actes-field">
              <span>{T('Catégorie', 'Category')}</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{T(c.fr, c.en)}</option>)}
              </select>
            </label>
          </div>

          {category === 'archives' && (
            <label className="actes-field">
              <span>{T("Type d'archives", 'Archive kind')}</span>
              <select value={subcategory} onChange={(e) => setSubcategory(e.target.value)}>
                {ARCHIVE_SUBS.map((s) => <option key={s.key} value={s.key}>{T(s.fr, s.en)}</option>)}
              </select>
            </label>
          )}

          <button className="btn btn-ghost btn-wide" onClick={() => void webLookup()} disabled={looking || !label.trim()}>
            <KBIcon name="globe" size={13} />
            {looking
              ? T('Recherche en cours…', 'Looking up…')
              : T('Vérifier sur le web (info officielle)', 'Check on the web (official info)')}
          </button>
          {note && <p className="muted-note ok-note">{note}</p>}

          <label className="actes-field">
            <span>{T('E-mail', 'Email')}</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@mairie-….fr" />
          </label>
          <div className="actes-grid2">
            <label className="actes-field">
              <span>{T('Téléphone', 'Phone')}</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <label className="actes-field">
              <span>{T('Département', 'Department')}</span>
              <input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="ex: Hauts-de-Seine (92)" />
            </label>
          </div>
          <label className="actes-field">
            <span>{T('Adresse', 'Address')}</span>
            <textarea rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>

          {editing && reference.me.isSuper && (
            <div className="actes-merge">
              <div className="actes-merge-h"><KBIcon name="layers" size={13} /> {T('Fusionner (dédoublonner)', 'Merge (dedupe)')}</div>
              <div className="actes-merge-row">
                <select value={mergeInto} onChange={(e) => setMergeInto(e.target.value)}>
                  <option value="">{T('Fusionner dans…', 'Merge into…')}</option>
                  {all.filter((a) => a.id !== initial.id).map((a) => (
                    <option key={a.id} value={a.id}>{a.label_fr}</option>
                  ))}
                </select>
                <button className="btn btn-sm btn-ghost" disabled={!mergeInto || busy} onClick={() => setConfirmMerge(true)}>
                  {T('Fusionner', 'Merge')}
                </button>
              </div>
              <p className="muted-note">
                {T('Toutes les demandes seront réaffectées vers l’autre autorité, puis cette fiche supprimée.',
                   'All requests will be reassigned to the other authority, then this entry is deleted.')}
              </p>
            </div>
          )}

          {err && <p className="muted-note ni-err">⚠ {err}</p>}

          <div className="nt-actions spread">
            {editing && reference.me.isStaff
              ? <button className="btn btn-ghost danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
                  <KBIcon name="trash" size={13} /> {T('Supprimer', 'Delete')}
                </button>
              : <span />}
            <span className="nt-actions-r">
              <button className="btn btn-ghost" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
              <button className="btn" disabled={busy || !label.trim()} onClick={() => void save()}>
                {busy ? '…' : editing ? T('Enregistrer', 'Save') : T('Créer', 'Create')}
              </button>
            </span>
          </div>
        </div>

        {confirmDelete && (
          <Ask lang={lang} busy={busy}
            title={T('Supprimer cette autorité ?', 'Delete this authority?')}
            body={label}
            onNo={() => setConfirmDelete(false)} onYes={() => void remove()} />
        )}
        {confirmMerge && (
          <Ask lang={lang} busy={busy}
            title={T('Confirmer la fusion ? Action irréversible.', 'Confirm merge? This cannot be undone.')}
            body={`${label} → ${all.find((a) => String(a.id) === mergeInto)?.label_fr ?? ''}`}
            onNo={() => setConfirmMerge(false)} onYes={() => void merge()} />
        )}
      </div>
    </div>,
    document.body,
  );
}

function Ask({ lang, title, body, busy, onYes, onNo }: {
  lang: Lang; title: string; body: string; busy: boolean; onYes: () => void; onNo: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  return (
    <div className="actes-ask">
      <div className="actes-ask-card">
        <h4>{title}</h4>
        <p className="muted-note">{body}</p>
        <div className="nt-actions">
          <button className="btn btn-ghost" onClick={onNo}>{T('Annuler', 'Cancel')}</button>
          <button className="btn" disabled={busy} onClick={onYes}>{T('Confirmer', 'Confirm')}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Fifteen at a time, reviewed before anything is written ─────────────── */
interface BatchRow {
  id: number; name: string; email: string; phone: string;
  department: string; address: string; include: boolean; found: boolean;
}

function BatchLookupModal({ lang, candidates, onClose, onSaved }: {
  lang: Lang; candidates: ActesRef[]; onClose: () => void; onSaved: () => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const LIMIT = 15;
  const targets = candidates.slice(0, LIMIT);
  const [phase, setPhase] = useState<'idle' | 'running' | 'review' | 'saving'>('idle');
  const [done, setDone] = useState(0);
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [err, setErr] = useState('');

  const run = async () => {
    setPhase('running'); setErr('');
    const out: BatchRow[] = [];
    for (let i = 0; i < targets.length; i++) {
      const a = targets[i];
      setDone(i + 1);
      try {
        const p = await actesLookupAuthority(a.label_fr);
        const found = !!(p.email || p.phone || p.department);
        out.push({
          id: a.id, name: a.label_fr, email: p.email ?? '', phone: p.phone ?? '',
          department: p.department ?? '', address: p.address ?? '', include: found, found,
        });
      } catch {
        out.push({ id: a.id, name: a.label_fr, email: '', phone: '', department: '', address: '', include: false, found: false });
      }
    }
    setRows(out); setPhase('review');
  };

  const save = async () => {
    setPhase('saving'); setErr('');
    try {
      for (const r of rows.filter((x) => x.include)) {
        await actesSaveAuthority(r.id, {
          email: r.email, phone: r.phone, department: r.department, address: r.address,
        });
      }
      onSaved();
    } catch (e) { setErr(friendlyError(e, lang)); setPhase('review'); }
  };

  const upd = (i: number, patch: Partial<BatchRow>) =>
    setRows((rs) => rs.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return createPortal(
    <div className="svc-modal-backdrop actes-skin" onClick={onClose}>
      <div className="svc-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <button className="svc-modal-close" onClick={onClose}>×</button>
        <div className="svc-modal-body">
          <div className="svc-modal-tag">{T('Autorités', 'Authorities')}</div>
          <h2>{T('Compléter les coordonnées via le web', 'Fill contacts via the web')}</h2>
          {phase === 'idle' && (
            <p className="muted-note">
              {T(`Recherche automatique des coordonnées officielles pour les ${targets.length} autorités les plus sollicitées sans e-mail. Vous validez avant d'enregistrer.`,
                 `Automatic lookup of official contacts for the ${targets.length} most-requested authorities without email. You review before saving.`)}
            </p>
          )}
          {phase === 'running' && (
            <p className="muted-note">{T(`Recherche ${done} / ${targets.length}…`, `Looking up ${done} / ${targets.length}…`)}</p>
          )}
          {(phase === 'review' || phase === 'saving') && rows.map((r, i) => (
            <div key={r.id} className={`actes-batch-row ${r.found ? '' : 'off'}`}>
              <label className="actes-check">
                <input type="checkbox" checked={r.include} disabled={!r.found}
                  onChange={(e) => upd(i, { include: e.target.checked })} />
                <strong>{r.name}</strong>
                {!r.found && <span className="dim">{T('rien trouvé', 'nothing found')}</span>}
              </label>
              {r.found && (
                <div className="actes-grid2">
                  <input value={r.email} placeholder={T('E-mail', 'Email')} onChange={(e) => upd(i, { email: e.target.value })} />
                  <input value={r.department} placeholder={T('Département', 'Department')} onChange={(e) => upd(i, { department: e.target.value })} />
                </div>
              )}
            </div>
          ))}
          {err && <p className="muted-note ni-err">⚠ {err}</p>}

          <div className="nt-actions">
            <button className="btn btn-ghost" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
            {phase === 'idle' && (
              <button className="btn" disabled={!targets.length} onClick={() => void run()}>
                <KBIcon name="globe" size={13} /> {T('Lancer la recherche', 'Start lookup')}
              </button>
            )}
            {(phase === 'review' || phase === 'saving') && (
              <button className="btn" disabled={phase === 'saving' || !rows.some((r) => r.include)} onClick={() => void save()}>
                {T('Enregistrer', 'Save')} {rows.filter((r) => r.include).length}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
